import React from 'react';
import { Card, Title, Grid, Text, Group, Divider, Stack, Box, Paper, ThemeIcon, Button } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { IconCurrencyDollar, IconShield, IconTruck } from '@tabler/icons-react';
import { CostSummaryProps } from './types';
import { useEffect } from 'react';
import PaymentCurrencyDisclaimerModal from '../payment/PaymentCurrencyDisclaimerModal';
import { usePaymentCurrencyDisclaimer } from '../../hooks/usePaymentCurrencyDisclaimer';
import { DEFAULT_EXCHANGE_RATES, CURRENCY } from '../../utils/currencyUtils';

const CostSummary: React.FC<CostSummaryProps> = ({ cost }) => {
    const formatCurrency = (amount: number) => `$${amount.toFixed(2)}`;
    const costItems = [
        { label: 'Shipping', amount: cost.shipping, icon: IconTruck, color: 'blue' },
        { label: 'Insurance', amount: cost.insurance, icon: IconShield, color: 'green' },
        ...(cost.consolidation ? [{ label: 'Consolidation Fee', amount: cost.consolidation, icon: IconCurrencyDollar, color: 'violet' }] : []),
        ...(cost.other ? [{ label: 'Other Fees', amount: cost.other, icon: IconCurrencyDollar, color: 'gray' }] : [])
    ];

    useEffect(() => {
        if (cost.paymentStatus === 'INITIATED' || cost.paymentStatus === 'QR_CODE_GENERATED') {
            showNotification({
                title: 'Payment Required',
                message: 'Please pay the shipping fees to proceed with your shipment',
                color: 'red',
                icon: <IconCurrencyDollar size={16} />,
                withCloseButton: true,
                autoClose: false,
                bg: 'yellow.0',
            });
        } else if (cost.paymentStatus === 'CONFIRMED') {
            showNotification({
                title: 'Payment Confirmed',
                message: 'Your payment has been received and confirmed.',
                color: 'green',
                withCloseButton: true,
            });
        } else if (cost.paymentStatus === 'FAILED') {
            showNotification({
                title: 'Payment Failed',
                message: 'Payment failed. Please try again or contact support.',
                color: 'red',
                withCloseButton: true,
                autoClose: false,
            });
        } else if (cost.paymentStatus === 'CANCELLED') {
            showNotification({
                title: 'Payment Cancelled',
                message: 'Payment was cancelled. You may retry the payment.',
                color: 'orange',
                withCloseButton: true,
                autoClose: false,
            });
        } else if (!cost.paymentStatus) {
            showNotification({
                title: 'Review Cost Summary',
                message: 'Review the cost summary and proceed to payment.',
                color: 'gray',
                withCloseButton: true,
                autoClose: false,
            });
        }
        // Only run on mount or when paymentStatus changes

    }, [cost.paymentStatus]);

    // Use the payment currency disclaimer hook
    const exchangeRate = DEFAULT_EXCHANGE_RATES[CURRENCY.LKR];

    // Define redirect function
    const redirectToPayment = () => {
        if (cost.url) {
            window.location.href = cost.url;
        } else {
            showNotification({
                title: 'Payment URL Unavailable',
                message: 'Payment URL is not available.',
                color: 'red',
            });
        }
    };

    // Use the currency disclaimer hook
    const {
        opened: disclaimerOpened,
        close: closeDisclaimer,
        showDisclaimer,
        handleProceed,
        amountUSD,
        amountLKR,
        exchangeRate: currentRate
    } = usePaymentCurrencyDisclaimer(redirectToPayment, exchangeRate);

    // Payment handlers (replace with real logic as needed)
    const handlePay = () => {
        // Show disclaimer modal instead of directly redirecting
        showDisclaimer(cost.total);
    };
    const handleRetry = () => {
        // Show disclaimer before retrying payment
        showNotification({
            title: 'Retrying Payment',
            message: 'Attempting to retry your payment...',
            color: 'blue',
        });

        // Show disclaimer modal instead of directly redirecting
        showDisclaimer(cost.total);
    };

    return (
        <Card shadow="sm" p={{ base: "sm", sm: "lg" }} radius="md" withBorder>
            <Group justify="space-between" align="center" mb="md">
                <Title order={3} p={{ base: "h4", sm: "h3" }}>Cost Summary</Title>
            </Group>

            {/* Mobile-First Layout */}
            <Box display={{ base: 'block', md: 'none' }}>
                <Stack gap="sm">
                    {costItems.map((item, index) => (
                        <Paper key={index} withBorder p="xs" radius="md" bg="gray.0">
                            <Group justify="space-between" align="center">
                                <Group gap="xs" align="center">
                                    <ThemeIcon size="sm" color={item.color} variant="light">
                                        <item.icon size={14} />
                                    </ThemeIcon>
                                    <Text size="sm" fw={500}>{item.label}</Text>
                                </Group>
                                <Text size="sm" fw={600}>{formatCurrency(item.amount)}</Text>
                            </Group>
                        </Paper>
                    ))}

                    <Divider my="xs" />

                    <Paper withBorder p="sm" radius="md" bg="blue.0">
                        <Group justify="space-between" align="center">
                            <Group gap="xs" align="center">
                                <ThemeIcon size="md" color="blue" variant="filled">
                                    <IconCurrencyDollar size={16} />
                                </ThemeIcon>
                                <Text fw={700} size="md">Total Amount</Text>
                            </Group>
                            <Text fw={700} size="lg" c="blue.7">
                                {formatCurrency(cost.total)}
                            </Text>
                        </Group>
                    </Paper>
                </Stack>
            </Box>

            {/* Desktop Layout */}
            <Box display={{ base: 'none', md: 'block' }}>
                <Stack gap="md">
                    {costItems.map((item, index) => (
                        <Grid key={index} gutter="md" align="center">
                            <Grid.Col span={6}>
                                <Group gap="xs" align="center">
                                    <ThemeIcon size="sm" color={item.color} variant="light">
                                        <item.icon size={16} />
                                    </ThemeIcon>
                                    <Text fz="md">{item.label}</Text>
                                </Group>
                            </Grid.Col>
                            <Grid.Col span={6} style={{ textAlign: 'right' }}>
                                <Text fz="md" fw={500}>{formatCurrency(item.amount)}</Text>
                            </Grid.Col>
                        </Grid>
                    ))}

                    <Divider my="md" />

                    <Grid gutter="md" align="center">
                        <Grid.Col span={6}>
                            <Group gap="xs" align="center">
                                <ThemeIcon size="md" color="blue" variant="filled">
                                    <IconCurrencyDollar size={18} />
                                </ThemeIcon>
                                <Text fw={700} size="lg">Total</Text>
                            </Group>
                        </Grid.Col>
                        <Grid.Col span={6} style={{ textAlign: 'right' }}>
                            <Text fw={700} size="xl" c="blue.7">
                                {formatCurrency(cost.total)}
                            </Text>
                        </Grid.Col>
                    </Grid>
                </Stack>
            </Box>

            {/* Payment Action Buttons */}
            <Box mt="md" style={{ textAlign: 'center' }}>
                {cost.paymentStatus === 'INITIATED' || cost.paymentStatus === undefined ? (
                    <Button color="blue" radius="md" size="md" fw={600} onClick={handlePay}>
                        Pay Now
                    </Button>
                ) : cost.paymentStatus !== 'CONFIRMED' && (
                    <Button color="red" radius="md" size="md" fw={600} onClick={handleRetry}>
                        Retry Payment
                    </Button>
                )}
            </Box>

            {/* Cost Breakdown Note */}
            <Box mt="md">
                <Text size="xs" c="dimmed" ta="center" style={{ fontStyle: 'italic' }}>
                    {cost.paymentStatus === 'INITIATED' && 'Your payment is initiated. Please complete the payment to proceed.'}
                    {cost.paymentStatus === 'CONFIRMED' && 'Your payment has been received and confirmed.'}
                    {cost.paymentStatus === 'FAILED' && 'Payment failed. Please try again or contact support.'}
                    {cost.paymentStatus === 'CANCELLED' && 'Payment was cancelled. You may retry the payment.'}
                    {!cost.paymentStatus && 'Review the cost summary and proceed to payment.'}
                </Text>
            </Box>

            {/* Payment Currency Disclaimer Modal */}
            <PaymentCurrencyDisclaimerModal
                opened={disclaimerOpened}
                onClose={closeDisclaimer}
                onProceed={handleProceed}
                amountUSD={amountUSD}
                amountLKR={amountLKR}
                exchangeRate={currentRate}
                isMobile={window.innerWidth < 768}
            />
        </Card>
    );
};

export default CostSummary;
